import React from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import { useLocale } from '../../../hooks/useLocale';

// Same AI icon the native field uses.
const AI_ICON = require('../../../../assets/onboarding/icon-choose-ai.png');
const aiIconSrc =
  typeof AI_ICON === 'string'
    ? AI_ICON
    : (AI_ICON as { uri?: string }).uri ?? (AI_ICON as string);

interface Props {
  value: string;
  onChangeText: (v: string) => void;
  onSubmit: () => void;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Web "Describe to AI" field: a violet→pink gradient-bordered pill with the AI
 * icon + an inline input, mirroring the native DescribeToAIField on the CV
 * experience/education forms. Submitting (Enter) drafts a new entry pre-filled
 * with the typed text. The gradient is the deliberate "AI input" brand accent
 * (matching native), not a generic CTA.
 */
export function DescribeToAIField({
  value,
  onChangeText,
  onSubmit,
  loading = false,
  disabled = false,
}: Props) {
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();

  const canSend = value.trim().length > 0 && !disabled && !loading;
  const handleSend = () => {
    if (canSend) onSubmit();
  };

  return (
    <div
      style={{
        borderRadius: 9999,
        padding: 2.5,
        background: 'linear-gradient(90deg, #b4a6f9 0%, #b4a6f9 42%, #e89bf0 100%)',
      }}
    >
      <div
        style={{
          height: 52,
          borderRadius: 9999,
          backgroundColor: 'var(--input-bg)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          paddingInline: 18,
        }}
      >
        {loading ? (
          <span
            aria-hidden
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid var(--text-tertiary)',
              borderTopColor: 'transparent',
              flexShrink: 0,
              animation: 'tabbio-spin 800ms linear infinite',
            }}
          />
        ) : (
          <img
            src={aiIconSrc}
            alt=""
            aria-hidden
            width={22}
            height={22}
            style={{ flexShrink: 0, objectFit: 'contain' }}
          />
        )}
        <input
          type="text"
          value={value}
          disabled={disabled || loading}
          onChange={(e) => onChangeText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={t('cv.edit.describeToAI', { defaultValue: 'Describe to AI' })}
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontFamily: 'inherit',
            fontSize: 15,
            color: 'var(--text-primary)',
            textAlign: isRTL ? 'right' : 'left',
            opacity: disabled || loading ? 0.65 : 1,
          }}
        />
        {canSend ? (
          <button
            type="button"
            onClick={handleSend}
            aria-label={t('cv.edit.describeToAISend', { defaultValue: 'Generate' })}
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginInlineEnd: -6,
            }}
          >
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        ) : null}
        <style>{`
          @keyframes tabbio-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
